import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { TransactionsList } from './TransactionsList';
import { TxHistoryScreen } from './TxHistoryScreen';
import { SendReceiveButtons } from './SendReceiveButtons';
import { ReauthFlow } from './ReauthFlow';
import { BottomSheet } from './BottomSheet';
import { NetworkSelect, NETWORK_ICON } from './NetworkSelect';
import { ChevronRight, CopyIcon, EyeIcon, EyeOffIcon } from './icons';
import { DEFAULT_COIN, COINS, coinKey, type Coin } from './TokenSheet';
import { useWallet } from '../../hooks/useWallet';
import { useMe } from '../../hooks/useMe';
import { useCoinMarket, type Timeframe } from '../../hooks/useCoinMarket';
import { useBackButton, hasTelegramBackButton } from '../../hooks/useBackButton';
import { getSeed, addWalletAddress, renameWalletAddress, deleteWalletAddress } from '../../lib/wallet';
import { ensureWalletAddresses } from '../../lib/addressBackfill';
import { addressDisplayName, shortenAddress } from '../../lib/walletNames';
import { HIDDEN_BALANCE, useUiStore } from '../../store/ui';
import { useMoneroBalance } from '../../hooks/useMoneroBalance';

// Genesis dates (immutable) for the stats row.
const CREATED: Record<Coin['symbol'], string> = {
  USDT: '14.11.2014',
  USDC: '26.09.2018',
  ETH: '30.07.2015',
  BTC: '03.01.2009',
  TRX: '25.06.2018',
  // BNB Smart Chain's genesis, not the 2017 BNB ERC-20 token sale — the coin
  // the wallet holds is the one that pays gas on this chain.
  BNB: '01.09.2020',
  SOL: '16.03.2020',
  HYPE: '29.11.2024',
  ZEC: '28.10.2016',
  TON: '15.08.2020',
  XMR: '18.04.2014',
  DOGE: '06.12.2013',
  XRP: '02.06.2012',
};

function EditIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
      <path d="M16.5 4.5l3 3L8 19l-3.5.5L5 16 16.5 4.5Z" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" />
    </svg>
  );
}

function CheckIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
      <path d="M5 12.5l4.5 4.5L19 7" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function PlusIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
      <path d="M12 5v14M5 12h14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
  );
}

function TrashIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
      <path d="M4 7h16M9 7V5h6v2M6 7l1 13h10l1-13" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

const short = shortenAddress;

function fmtPrice(p: number | null): string {
  if (p == null) return '—';
  return `$${p.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 4 })}`;
}

function fmtCompact(n: number | null): string {
  if (n == null) return '—';
  if (n >= 1e9) return `$${(n / 1e9).toFixed(2)}B`;
  if (n >= 1e6) return `$${(n / 1e6).toFixed(2)}M`;
  if (n >= 1e3) return `$${(n / 1e3).toFixed(2)}K`;
  return `$${n.toFixed(0)}`;
}

function PriceChart({ points }: { points: [number, number][] }) {
  const W = 320;
  const H = 120;
  // Keep stroke (2px) inside the viewBox — max/min used to sit on y=0 / y=H and clipped.
  const PAD = 6;
  if (points.length < 2) return <div className="h-[120px]" />;

  const prices = points.map((p) => p[1]);
  const min = Math.min(...prices);
  const max = Math.max(...prices);
  const range = max - min || 1;
  const up = prices[prices.length - 1]! >= prices[0]!;
  const color = up ? '#69cc8b' : '#ff5a5a';

  const xy = points.map((p, i) => {
    const x = (i / (points.length - 1)) * W;
    const y = H - PAD - ((p[1] - min) / range) * (H - 2 * PAD);
    return [x, y] as const;
  });
  const line = xy.map((p, i) => `${i ? 'L' : 'M'}${p[0].toFixed(1)} ${p[1].toFixed(1)}`).join(' ');
  const area = `${line} L${W} ${H} L0 ${H} Z`;

  const dec = max < 10 ? 4 : 2;
  const yLabels = [max, max - range / 3, min + range / 3, min].map((v) => v.toFixed(dec));
  const fmtT = (ts: number, withYear = false) =>
    new Date(ts).toLocaleDateString(
      undefined,
      withYear ? { day: 'numeric', month: 'short', year: 'numeric' } : { day: 'numeric', month: 'short' },
    );

  return (
    <div className="flex flex-col gap-3">
      <div className="flex gap-3">
        <div className="relative h-[120px] flex-1">
          <div className="absolute inset-0 flex flex-col justify-between">
            {yLabels.map((_, i) => (
              <div key={i} className="border-t border-dashed border-black/[0.1]" />
            ))}
          </div>
          <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" className="absolute inset-0 h-full w-full">
            <defs>
              <linearGradient id="coinArea" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={color} stopOpacity="0.28" />
                <stop offset="100%" stopColor={color} stopOpacity="0" />
              </linearGradient>
            </defs>
            <path d={area} fill="url(#coinArea)" />
            <path d={line} fill="none" stroke={color} strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" vectorEffect="non-scaling-stroke" />
          </svg>
        </div>
        <div className="flex w-12 flex-col justify-between text-right text-[11px] tabular-nums text-black/[0.44]">
          {yLabels.map((l, i) => (
            <span key={i}>{l}</span>
          ))}
        </div>
      </div>
      <div className="flex">
        <div className="flex flex-1 justify-between text-[12px] text-black/[0.44]">
          <span>{fmtT(points[0]![0], true)}</span>
          <span>{fmtT(points[points.length - 1]![0])}</span>
        </div>
        <div className="w-12" />
      </div>
    </div>
  );
}

const TIMEFRAMES = ['tf1d', 'tf7d', 'tf1m', 'tf1y', 'tfAll'] as const;
// 1d→hourly·24h, 7d→4h·7d, 1m→daily·30d, 1y→weekly·1y, all→monthly (server-side).
const TF_KEYS: Timeframe[] = ['1d', '7d', '1m', '1y', 'all'];

export function CoinInfoScreen({
  onClose,
  coin = DEFAULT_COIN,
  onSend,
  onReceive,
  onSwap,
}: {
  onClose: () => void;
  coin?: Coin;
  onSend?: (coin: Coin) => void;
  onReceive?: (coin: Coin) => void;
  onSwap?: (coin: Coin) => void;
}) {
  const { t } = useTranslation();
  useBackButton(onClose);
  const tgBack = hasTelegramBackButton();
  const [tf, setTf] = useState(0);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [balanceOpen, setBalanceOpen] = useState(false);
  const [editing, setEditing] = useState<string | null>(null);
  const [names, setNames] = useState<Record<string, string>>({});
  const [copied, setCopied] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  // Pending sensitive action awaiting reauth: add a new address, or delete one.
  const [reauthAction, setReauthAction] = useState<
    { type: 'add' } | { type: 'delete'; addrId: string } | null
  >(null);
  // Address id awaiting delete confirmation (warning sheet).
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // Network switcher — user can flip between chains for the same token symbol.
  const [activeChain, setActiveChain] = useState<Coin['chain']>(coin.chain);
  const activeCoin = COINS.find((c) => c.symbol === coin.symbol && c.chain === activeChain) ?? coin;
  const altCoins = COINS.filter((c) => c.symbol === coin.symbol);
  const NetworkIcon = NETWORK_ICON[activeCoin.chain];
  const coinId = coinKey(activeCoin);
  const balanceHidden = useUiStore((s) => !!s.coinBalanceHidden[coinId]);
  const toggleCoinBalanceHidden = useUiStore((s) => s.toggleCoinBalanceHidden);

  const { wallet, balances } = useWallet();
  const { me, initData, canFetch } = useMe();
  const qc = useQueryClient();
  const market = useCoinMarket(activeCoin, TF_KEYS[tf]!);
  // Line chart from the candle closes (timestamp, close) at the timeframe's
  // granularity (hourly / 4h / daily / weekly / monthly).
  const points = market.candles.map((c) => [c[0], c[4]] as [number, number]);

  const coinBalance = balances
    .filter((b) => b.chain === activeCoin.chain && b.token === activeCoin.symbol)
    .reduce((s, b) => s + Number(b.raw) / 10 ** b.decimals, 0);
  const usdValue = coinBalance * (market.price ?? 0);
  const addresses = (wallet?.addresses ?? []).filter((a) => a.chain === activeCoin.chain);

  // Monero is the one coin whose balance this screen has to go and find. The
  // server cannot compute it, so opening this screen starts the scan; the
  // result is published to the query cache and useWallet folds it into
  // `balances`, which is where `coinBalance` above reads from. Auto only while
  // the Monero screen is actually open — a scan is minutes of work on a cold
  // wallet and must not run because the user looked at Bitcoin.
  const xmrAddress = activeCoin.chain === 'xmr' ? (addresses[0]?.eoaAddress ?? null) : null;
  const xmrScan = useMoneroBalance(xmrAddress, addresses[0]?.createdAt ?? null, {
    auto: activeCoin.chain === 'xmr',
  });

  // Per-wallet balance for this coin (balances are keyed by eoaAddress server-side).
  const balanceOf = (addr: string) =>
    balances
      .filter((b) => b.chain === activeCoin.chain && b.token === activeCoin.symbol && b.address === addr)
      .reduce((s, b) => s + Number(b.raw) / 10 ** b.decimals, 0);
  // Richest first; same balance → derivation index so Wallet 1 stays above Wallet 2.
  const addressesByBalance = [...addresses].sort((a, b) => {
    const diff = balanceOf(b.eoaAddress) - balanceOf(a.eoaAddress);
    return diff !== 0 ? diff : a.derivationIndex - b.derivationIndex;
  });
  // BTC needs 8 fraction digits (6 would hide sat-level balances).
  const fmtAmount = (n: number) =>
    n.toLocaleString('en-US', { maximumFractionDigits: activeCoin.symbol === 'BTC' ? 8 : 6 });

  // Shared naming (same as the address selector) so screens never disagree; the
  // in-progress rename value (names[a.id]) overlays it while editing.
  const displayName = (a: { id: string; label: string | null; derivationIndex: number }) =>
    names[a.id] ?? addressDisplayName(a, t('nav.wallet'));

  const showToast = (msg: string) => {
    setToast(msg);
    window.setTimeout(() => setToast((m) => (m === msg ? null : m)), 1800);
  };

  const copyAddress = (addr: string) => {
    navigator.clipboard?.writeText(addr).then(() => {
      setCopied(addr);
      window.setTimeout(() => setCopied((c) => (c === addr ? null : c)), 1500);
      showToast(t('coin.copied'));
    }, () => {});
  };

  const needsReauth = !!me?.pinSet || !!me?.biometricEnabled;
  const refreshWallet = () => qc.invalidateQueries({ queryKey: ['wallet'] });
  // True once the silent create has finished and the chain still has no address,
  // so the row stops saying "loading" and the button can create it.
  const [fillDone, setFillDone] = useState(false);

  // Existing wallets predate these chains, so the row is empty until the
  // address is registered. Boot does this when the seed is already in hand;
  // opening the coin retries it, because a failed attempt used to leave this
  // screen on "loading" with the add button disabled.
  const addressTry = useRef(new Set<string>());
  useEffect(() => {
    if (!canFetch || !wallet || addresses.length > 0 || needsReauth) return;
    if (addressTry.current.has(activeCoin.chain)) return;
    addressTry.current.add(activeCoin.chain);
    const snapshot = wallet;
    setFillDone(false);
    void (async () => {
      try {
        const mnemonic = await getSeed(initData).catch(() => null);
        if (mnemonic) await ensureWalletAddresses(initData, mnemonic, snapshot, refreshWallet);
      } finally {
        setFillDone(true);
      }
    })();
    // refreshWallet is recreated each render; the ref above makes one attempt.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canFetch, wallet, addresses.length, activeCoin.chain, needsReauth, initData]);

  // Add a fresh receiving address: derive the next index client-side, register it.
  const doAdd = async (token?: string) => {
    if (!canFetch || busy) return;
    setBusy(true);
    try {
      const mnemonic = await getSeed(initData, token);
      if (!mnemonic) {
        // Backend, DB and derivation are all fine here — a null seed means the
        // encrypted seed isn't available/decryptable on THIS device/session
        // (e.g. opened outside the original Telegram app). Say so clearly.
        console.error('[addWallet] getSeed returned null — seed unavailable on this device');
        showToast(t('coin.seedUnavailable'));
        return;
      }
      // Next free index for this chain. Retry on a unique collision (stale cache
      // / concurrent add) by bumping the index instead of failing.
      const start = (addresses.length ? Math.max(...addresses.map((a) => a.derivationIndex)) : -1) + 1;
      for (let i = 0; i < 3; i++) {
        try {
      await addWalletAddress(initData, mnemonic, activeCoin.chain, start + i);
          refreshWallet();
          showToast(t('coin.walletAdded'));
          return;
        } catch (e) {
          if (String((e as Error)?.message ?? '').includes('duplicate')) continue;
          throw e;
        }
      }
      showToast(t('coin.addFailed'));
    } catch (err) {
      console.error('[addWallet] failed — chain:', activeCoin.chain, 'err:', err);
      const msg = String((err as Error)?.message ?? '');
      showToast(msg.includes('too_many_addresses') ? t('coin.tooManyWallets') : t('coin.addFailed'));
    } finally {
      setBusy(false);
    }
  };

  const addWallet = () => {
    if (needsReauth) setReauthAction({ type: 'add' });
    else void doAdd();
  };

  // Actually delete (after warning confirm + reauth when required).
  const doRemove = async (addrId: string, token?: string) => {
    if (!canFetch || busy) return;
    setBusy(true);
    try {
      await deleteWalletAddress(initData, addrId, token);
      refreshWallet();
      showToast(t('coin.walletDeleted'));
    } catch {
      showToast(t('coin.deleteFailed'));
    } finally {
      setBusy(false);
    }
  };

  // Trash tapped → open the warning sheet (don't delete yet).
  const askRemove = (addrId: string) => {
    setEditing(null);
    setConfirmDeleteId(addrId);
  };

  // Warning confirmed → gate with reauth when a PIN/biometric is set.
  const onConfirmRemove = () => {
    const addrId = confirmDeleteId;
    setConfirmDeleteId(null);
    if (!addrId) return;
    if (needsReauth) setReauthAction({ type: 'delete', addrId });
    else void doRemove(addrId);
  };

  const commitRename = (a: { id: string; label: string | null }) => {
    setEditing(null);
    const next = (names[a.id] ?? '').trim();
    if (canFetch && next && next !== (a.label ?? '')) {
      void renameWalletAddress(initData, a.id, next).then(refreshWallet).catch(() => {});
    }
  };

  // Round first so a tiny negative (stablecoins barely move) doesn't show "-0.00%".
  const pct = market.change24h == null ? null : Number(market.change24h.toFixed(2));
  const changeUp = (pct ?? 0) >= 0;

  return (
    <div className="relative flex min-h-screen flex-col overflow-x-hidden bg-surface">
      {/* TOP CARD */}
      <div className="rounded-b-[32px] bg-white px-4 pb-6 pt-[calc(var(--tg-safe-top)_+_0.75rem)] shadow-[0px_4px_24px_rgba(0,0,0,0.05)]">
        {!tgBack && (
          <div className="flex h-10 items-center">
            <button
              onClick={onClose}
              className="flex h-9 w-9 items-center justify-center rounded-full bg-black/[0.06] text-ink"
              aria-label="Back"
            >
              <ChevronRight size={18} className="rotate-180 text-ink" />
            </button>
          </div>
        )}

        <div className="mt-2 flex items-center justify-between gap-3">
          <div className="flex min-w-0 items-center gap-3">
            <span className="relative shrink-0">
              <activeCoin.Icon size={54} />
              {altCoins.length > 1 ? (
                <span
                  className="pointer-events-none absolute bottom-0 right-0 flex rounded-full bg-white p-0.5"
                  aria-hidden
                >
                  <NetworkIcon size={20} className="block" />
                </span>
              ) : null}
            </span>
            <div className="flex min-w-0 flex-col gap-2">
              <span className="text-[16px] font-medium leading-none text-ink">{activeCoin.name}</span>
              <span className="text-[14px] font-medium leading-none text-muted">
                {activeCoin.symbol}
              </span>
            </div>
          </div>
          <NetworkSelect
            coins={altCoins}
            value={activeChain}
            onChange={setActiveChain}
            className="flex shrink-0 items-center gap-1 rounded-full bg-[#f0f1f4] py-1.5 pl-3 pr-2 active:opacity-70"
          />
        </div>
        <div className="mt-3 flex items-center gap-3">
          <span className="text-[28px] font-medium leading-none tabular-nums text-ink">
            {fmtPrice(market.price)}
          </span>
          {pct != null && (
            <span
              className="rounded-full px-2.5 py-2 text-[14px] font-medium leading-none"
              style={{
                color: changeUp ? '#69cb8b' : '#ff5a5a',
                background: changeUp ? 'rgba(105,203,139,0.1)' : 'rgba(255,90,90,0.1)',
              }}
            >
              {pct > 0 ? '+' : pct < 0 ? '−' : ''}
              {Math.abs(pct).toFixed(2)}%
            </span>
          )}
        </div>

        <div className="relative mt-5">
          <PriceChart points={points} />
          {market.chartLoading && (
            <div className="absolute inset-0 flex items-center justify-center">
              <span className="h-6 w-6 animate-spin rounded-full border-2 border-ink/15 border-t-ink" />
            </div>
          )}
        </div>

        <div className="mt-5 flex items-center rounded-full bg-[rgba(238,239,244,0.6)] p-1">
          {TIMEFRAMES.map((key, i) => (
            <button
              key={key}
              onClick={() => setTf(i)}
              className={`flex-1 rounded-full py-2.5 text-[14px] font-medium transition-colors ${
                tf === i ? 'bg-white text-ink shadow-sm' : 'text-black/[0.56]'
              }`}
            >
              {t(`coin.${key}`)}
            </button>
          ))}
        </div>
      </div>

      {/* BOTTOM CARD */}
      <div className="mt-2.5 flex-1 rounded-t-[32px] bg-white px-4 pb-28 pt-4 shadow-[0px_-4px_24px_rgba(0,0,0,0.04)]">
        {/* Balance accordion */}
        <div className="overflow-hidden rounded-[24px] bg-white shadow-[0px_4px_20px_rgba(0,0,0,0.08)]">
          <div className="flex h-16 w-full items-center px-4">
            <span className="text-[16px] text-ink">{t('coin.yourBalance')}</span>
            <button
              type="button"
              onClick={() => toggleCoinBalanceHidden(coinId)}
              aria-label={balanceHidden ? t('wallet.showBalance') : t('wallet.hideBalance')}
              className="ml-1.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-muted active:bg-black/[0.05]"
            >
              {balanceHidden ? <EyeOffIcon size={22} /> : <EyeIcon size={22} />}
            </button>
            <button
              type="button"
              onClick={() => setBalanceOpen((o) => !o)}
              className="ml-auto flex min-w-0 items-center gap-2"
              aria-expanded={balanceOpen}
            >
              <span className="flex flex-col items-end">
                <span className="text-[16px] font-medium tabular-nums text-ink">
                  {balanceHidden ? HIDDEN_BALANCE : `$${usdValue.toFixed(2)}`}
                </span>
                <span className="text-[12px] tabular-nums text-muted">
                  {balanceHidden
                    ? '••••'
                    : `${fmtAmount(coinBalance)} ${activeCoin.symbol}`}
                </span>
                {/* Monero only. Every other balance is already final when it
                    renders; this one is being searched for, block by block, and
                    silence here reads as "you have nothing" rather than "still
                    counting". */}
                {xmrScan.scanning && !balanceHidden ? (
                  <span className="text-[11px] tabular-nums text-muted">
                    {t('coin.scanning', { percent: xmrScan.percent })}
                  </span>
                ) : null}
              </span>
              <ChevronRight
                size={18}
                className={`text-muted transition-transform duration-500 ${balanceOpen ? 'rotate-90' : ''}`}
              />
            </button>
          </div>
          <div className={`grid transition-[grid-template-rows] duration-300 ease-out ${balanceOpen ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'}`}>
            <div className="overflow-hidden">
              <div className="px-4 pb-1">
                {addresses.length === 0 ? (
                  <div className="border-t border-black/[0.06] py-4 text-[13px] text-muted">
                    {fillDone || needsReauth ? '' : t('common.loading')}
                  </div>
                ) : (
                  addressesByBalance.map((a) => {
                    const name = displayName(a);
                    return (
                      <div key={a.id} className="flex items-center gap-3 border-t border-black/[0.06] py-3">
                        <activeCoin.Icon size={40} />
                        <div className="flex min-w-0 flex-1 flex-col gap-1">
                          {editing === a.id ? (
                            <div className="flex items-center gap-2">
                              <input
                                autoFocus
                                value={name}
                                onChange={(e) => setNames((p) => ({ ...p, [a.id]: e.target.value }))}
                                onBlur={() => commitRename(a)}
                                onKeyDown={(e) => e.key === 'Enter' && commitRename(a)}
                                className="min-w-0 flex-1 border-b border-ink/20 bg-transparent text-[16px] font-medium text-ink outline-none"
                              />
                              <button
                                onMouseDown={(e) => e.preventDefault()}
                                onClick={() => commitRename(a)}
                                className="shrink-0 text-[#377dff] active:opacity-70"
                                aria-label="Save"
                              >
                                <CheckIcon />
                              </button>
                              {a.derivationIndex > 0 && (
                                <button
                                  onMouseDown={(e) => e.preventDefault()}
                                  onClick={() => askRemove(a.id)}
                                  className="shrink-0 text-[#ff3b30] active:opacity-70"
                                  aria-label="Delete"
                                >
                                  <TrashIcon />
                                </button>
                              )}
                            </div>
                          ) : (
                            <button
                              onClick={() => setEditing(a.id)}
                              className="flex items-center gap-1.5 text-[16px] font-medium text-ink active:opacity-70"
                            >
                              {name}
                              <span className="text-muted">
                                <EditIcon />
                              </span>
                            </button>
                          )}
                          <span className="text-[12px] tabular-nums text-muted">
                            {balanceHidden
                              ? '••••'
                              : `${fmtAmount(balanceOf(a.eoaAddress))} ${activeCoin.symbol}`}
                          </span>
                        </div>
                        {editing !== a.id && (
                          <button
                            onClick={() => copyAddress(a.eoaAddress)}
                            className="flex items-center gap-2 active:opacity-70"
                            aria-label="Copy address"
                          >
                            <span className="text-[13px] tabular-nums text-muted">{short(a.eoaAddress)}</span>
                            <CopyIcon size={20} className={copied === a.eoaAddress ? 'text-positive' : 'text-[#377dff]'} />
                          </button>
                        )}
                      </div>
                    );
                  })
                )}

                {/* Add another receiving address (derive next index + register) */}
                <button
                  onClick={addWallet}
                  disabled={busy || (addresses.length === 0 && !needsReauth && !fillDone)}
                  className="flex w-full items-center gap-3 border-t border-black/[0.06] py-3 text-left active:opacity-70 disabled:opacity-50"
                >
                  <span className="flex h-10 w-10 items-center justify-center rounded-full bg-[#eef2ff] text-[#377dff]">
                    <PlusIcon />
                  </span>
                  <span className="text-[15px] font-medium text-[#377dff]">
                    {busy ? t('coin.creatingWallet') : t('coin.addWallet')}
                  </span>
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Operations — all networks for this symbol (network tab only scopes balance). */}
        <div className="mt-6">
          <TransactionsList
            max={3}
            light
            filter={{ token: activeCoin.symbol }}
            onSeeAll={() => setHistoryOpen(true)}
          />
        </div>

        {/* Statistics */}
        <h2 className="mt-7 text-[16px] font-medium text-ink">{t('coin.statistics')}</h2>
        <div className="mt-3 rounded-[24px] bg-[#f6f7f8] px-4 py-2.5">
          <StatRow label={t('coin.marketCap')} value={fmtCompact(market.marketCap)} />
          <StatRow label={t('coin.volume')} value={fmtCompact(market.volume)} />
          <StatRow label={t('coin.created')} value={CREATED[activeCoin.symbol]} />
          <StatRow label={t('coin.network')} value={activeCoin.network} last />
        </div>

        {/* About */}
        <h2 className="mt-7 text-[16px] font-medium text-ink">{t('coin.aboutTitle')}</h2>
        <div className="mt-3 rounded-[24px] bg-[#f6f7f8] p-4">
          <p className="text-[14px] leading-[1.5] text-ink">
            {t(`coin.about.${activeCoin.symbol}`)}
          </p>
        </div>
      </div>

      {/* Sticky actions — hidden while renaming (keyboard open) */}
      {editing === null && (
        <div className="fixed inset-x-0 bottom-0 z-20 px-4 pb-5 pt-3">
          <SendReceiveButtons
            onReceive={() => onReceive?.(activeCoin)}
            onSend={() => onSend?.(activeCoin)}
            onSwap={() => onSwap?.(activeCoin)}
            onLight
          />
        </div>
      )}

      {/* Delete confirmation — warning before a sensitive, reauth-gated action */}
      <BottomSheet
        open={confirmDeleteId !== null}
        onClose={() => setConfirmDeleteId(null)}
        title={t('coin.deleteTitle')}
      >
        <p className="mt-3 text-[14px] leading-[1.5] text-muted">{t('coin.deleteWarning')}</p>
        <div className="mt-6 flex items-center gap-3">
          <button
            onClick={() => setConfirmDeleteId(null)}
            className="h-14 flex-[2] rounded-full bg-[#f2f3f4] text-[15px] font-medium text-ink active:bg-black/[0.06]"
          >
            {t('common.cancel')}
          </button>
          <button
            onClick={onConfirmRemove}
            className="flex h-14 flex-[3] items-center justify-center gap-2 rounded-full bg-[#ff3b30] text-[15px] font-medium text-white active:opacity-90"
          >
            <TrashIcon />
            {t('coin.deleteConfirm')}
          </button>
        </div>
      </BottomSheet>

      {/* Full operations history for this coin (pushed over the detail screen) */}
      {historyOpen && (
        <div className="push-enter fixed inset-0 z-[60] overflow-y-auto bg-surface">
          <TxHistoryScreen
            filter={{ token: activeCoin.symbol }}
            title={`${activeCoin.symbol} · ${t('wallet.operations')}`}
            onClose={() => setHistoryOpen(false)}
          />
        </div>
      )}

      {/* Reauth before the pending sensitive action (when a PIN/biometric is set) */}
      {reauthAction && canFetch && (
        <div className="push-enter fixed inset-0 z-[60] overflow-y-auto bg-holo">
          <ReauthFlow
            initData={initData}
            biometricEnabled={!!me?.biometricEnabled}
            onSuccess={(token) => {
              const action = reauthAction;
              setReauthAction(null);
              if (action.type === 'add') void doAdd(token);
              else void doRemove(action.addrId, token);
            }}
            onCancel={() => setReauthAction(null)}
          />
        </div>
      )}

      {toast && (
        <div className="pointer-events-none fixed inset-x-0 top-0 z-[70] flex justify-center px-4 pt-[calc(var(--tg-safe-top)_+_1rem)]">
          <div className="animate-toast flex items-center gap-2 rounded-full bg-ink px-4 py-2.5 text-[14px] font-medium text-white shadow-[0px_10px_30px_rgba(0,0,0,0.25)]">
            {toast}
          </div>
        </div>
      )}
    </div>
  );
}

function StatRow({ label, value }: { label: string; value: string; last?: boolean }) {
  return (
    <div className="flex items-center justify-between py-2">
      <span className="text-[14px] text-muted">{label}</span>
      <span className="text-[14px] font-medium text-ink">{value}</span>
    </div>
  );
}
